import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import ActivityBadge from '@/components/ui/activity-badge';
import { DemoPill, StateMessage } from '@/components/ui/controls';
import { NotYet } from '@/components/ui/not-yet';
import { Badge, Card, Field, PageHeader, SubNav } from '@/components/ui/page';
import { SegmentChip } from '@/components/ui/segment-chip';
import { showDemo } from '@/constants/routes';
import Sparkline from '@/components/ui/sparkline';
import StatTile from '@/components/ui/stat-tile';
import { btn, table } from '@/components/ui/styles';
import { mockOwnerMessages, mockStatusTimeline, type MockOwnerMessage } from '@/mocks/casino-detail';
import { leadSearchLink } from '@/mocks/shared';
import type { CasinoCard, CasinoDetail } from '@/types/api';

import type { Segment } from '../segments/api-types';
import { countryName, formatCount, formatDate, formatDateTime, formatMoney, formatMoneyFull } from '@/utils/format';
import { casinoStatusLabel } from '@/utils/labels';
import { useApi } from '@/utils/use-api';

type Tab = 'overview' | 'segments' | 'messages' | 'timeline';
const TABS: Tab[] = ['overview', 'segments', 'messages', 'timeline'];

const MadeUp = ({ what }: { what: string }) => <DemoPill title={`${what} are made up until this part of Prisma is built.`}>Demo</DemoPill>;

/** The width of an element, kept up to date, so a chart can fill a phone screen (UI-1). */
const useWidth = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width] as const;
};

/**
 * One series over 30 days: the latest day and the peak as text, the line for the shape.
 * A single series needs no legend. The line is 420px wide, or the card's width on a phone.
 */
const TrendCard = ({ title, values, format, mock }: { title: string; values: number[]; format: (v: number) => string; mock: boolean }) => {
  const [ref, width] = useWidth();
  return (
    <Card title={title} aside={mock && <DemoPill title="Daily numbers are made up: this is the local mock metrics source.">Demo</DemoPill>}>
      <p className="text-sm text-fg-muted">
        Yesterday <span className="text-fg-strong">{format(values[values.length - 1])}</span> · peak {format(Math.max(...values))}
      </p>
      <div ref={ref} className="mt-4 h-24">
        {width > 0 && <Sparkline values={values} format={format} label={`${title} of the last 30 days`} width={Math.min(width, 420)} height={96} />}
      </div>
    </Card>
  );
};

/** The last 30 days (CAS-1, SEG-1): totals from the metrics source, the daily trend, and the casino's details. */
const Overview = ({ casino, mock }: { casino: CasinoCard; mock: boolean }) => {
  const m = casino.metrics;
  return (
    <div className="space-y-6">
      <section>
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="text-sm font-medium text-fg-strong">Last 30 days</h2>
          {mock && <DemoPill title="GGR, players and velocity are made-up numbers: this is the local mock metrics source.">Demo metrics</DemoPill>}
        </div>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatTile label="GGR, last 30 days" value={m ? formatMoney(m.ggr_30d_usd) : '—'} note="USD" />
          <StatTile label="Players, last 30 days" value={m ? formatCount(m.players_30d) : '—'} note="Busiest day" />
          <StatTile label="Velocity" value={m ? m.velocity.toFixed(1) : '—'} note="Activity score, 0–100" />
          <StatTile label="Live since" value={casino.launched_at ? formatDate(casino.launched_at) : '—'} />
        </div>
      </section>

      {m ? (
        <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
          <TrendCard title="Daily GGR" values={m.trend} format={formatMoneyFull} mock={mock} />
          <TrendCard title="Daily players" values={m.players_trend} format={formatCount} mock={mock} />
        </div>
      ) : (
        <StateMessage title="No metrics yet">{casino.status === 'ok' ? 'The metrics source knows nothing about this casino.' : 'Metrics start once the casino is live.'}</StateMessage>
      )}

      <Card title="Details">
        <dl>
          <Field label="Owner">
            {casino.owner_email ? (
              <Link to={leadSearchLink(casino.owner_email)} className="text-brand [overflow-wrap:anywhere] hover:underline">
                {casino.owner_name ? `${casino.owner_name} · ` : ''}
                {casino.owner_email}
              </Link>
            ) : (
              <span className="text-fg-faint">Not linked</span>
            )}
          </Field>
          <Field label="Domain">{casino.domain || '—'}</Field>
          <Field label="Country">{casino.country ? countryName(casino.country) : '—'}</Field>
          <Field label="Payments">
            {casino.operating_model === 'crypto' ? 'Takes crypto' : casino.operating_model === 'fiat' ? 'Fiat only' : <span className="text-fg-faint">Unknown</span>}
          </Field>
          <Field label="License">{casino.license === null ? <span className="text-fg-faint">Unknown</span> : casino.license || 'None'}</Field>
          <Field label="Status">{casinoStatusLabel(casino.status)}</Field>
          <Field label="Tenant">{casino.tenant_id || '—'}</Field>
        </dl>
      </Card>
    </div>
  );
};

/** The segments the casino falls in now (GET /casinos/{id}/segments), which decide the messages its owner gets (SEG-1…3). */
const Segments = ({ casino }: { casino: CasinoCard }) => {
  const { data, error } = useApi<Segment[]>(`/casinos/${encodeURIComponent(casino.id)}/segments`);
  const segments = data ?? [];
  return (
    <Card title="Segments">
      {error && !data ? (
        <p className="text-sm text-danger">The segments could not be loaded.</p>
      ) : !data ? (
        <div className="h-16 animate-pulse rounded-lg bg-layer" />
      ) : segments.length ? (
        <ul className="divide-y divide-line">
          {segments.map((s) => (
            <li key={s.id} className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0">
              <div className="min-w-0">
                <SegmentChip name={s.name} color={s.color} />
                {s.description && <p className="mt-1.5 text-sm text-fg-muted">{s.description}</p>}
              </div>
              <Link to={`/segments/${s.id}`} className={`${btn.link} inline-flex min-h-11 shrink-0 items-center md:min-h-0`}>
                Open segment
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-fg-faint">In no segment yet.</p>
      )}
      <p className="mt-4 text-xs text-fg-faint">Segments group casinos by performance, geography and type; a message targets one or more of them (SEG-3).</p>
    </Card>
  );
};

const CHANNEL: Record<MockOwnerMessage['channels'][number], string> = { email: 'Email', telegram: 'Telegram', popup: 'Owner Hub notification' };
const OUTCOME: Record<MockOwnerMessage['outcome'], { label: string; tone: 'neutral' | 'info' | 'ok' }> = {
  delivered: { label: 'Delivered', tone: 'neutral' },
  opened: { label: 'Opened', tone: 'info' },
  clicked: { label: 'Clicked', tone: 'ok' },
};

/** What the owner received from us, on every channel (OUT-1…6). A phone shows a list instead of the table (UI-1). */
const Messages = ({ casino }: { casino: CasinoCard }) => {
  if (!showDemo) return <StateMessage title="No data yet">A later version lists the messages Prisma sent this owner, on every channel.</StateMessage>;
  const messages = mockOwnerMessages(casino);
  return (
    <NotYet what="history" info="right-4 top-5">
      <div className={table.wrap}>
        <div className="flex items-center justify-between gap-2 pl-4 pr-10 pt-4">
          <h2 className="min-w-0 text-sm font-medium text-fg-strong [overflow-wrap:anywhere]">Messages to {casino.owner_email || 'the owner'}</h2>
          <MadeUp what="Messages" />
        </div>
        <ul className="mt-2 md:hidden">
          {messages.map((m) => (
            <li key={m.id} className="border-t border-line px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <p className="min-w-0 text-sm text-fg-strong">{m.title}</p>
                <Badge tone={OUTCOME[m.outcome].tone}>{OUTCOME[m.outcome].label}</Badge>
              </div>
              <p className="mt-0.5 text-xs text-fg-muted">
                {m.channels.map((ch) => CHANNEL[ch]).join(', ')} · {formatDateTime(m.sentAt)}
              </p>
            </li>
          ))}
        </ul>
        <div className="hidden overflow-x-auto md:block">
          <table className={`${table.table} min-w-[640px]`}>
            <thead className={table.head}>
              <tr>
                <th scope="col" className={table.th}>Message</th>
                <th scope="col" className={table.th}>Channels</th>
                <th scope="col" className={table.th}>Sent</th>
                <th scope="col" className={table.th}>Outcome</th>
              </tr>
            </thead>
            <tbody>
              {messages.map((m) => (
                <tr key={m.id} className={table.row}>
                  <td className={`${table.td} text-fg-strong`}>{m.title}</td>
                  <td className={`${table.td} text-fg-muted`}>{m.channels.map((c) => CHANNEL[c]).join(', ')}</td>
                  <td className={`${table.td} whitespace-nowrap tabular-nums text-fg-muted`}>{formatDateTime(m.sentAt)}</td>
                  <td className={table.td}>
                    <Badge tone={OUTCOME[m.outcome].tone}>{OUTCOME[m.outcome].label}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </NotYet>
  );
};

/** How the casino got here. Status changes come from Owner Hub only, never from chat (CAS-2, CAS-4). */
const Timeline = ({ casino }: { casino: CasinoCard }) => {
  if (!showDemo) return <StateMessage title="No data yet">A later version lists each status change the Owner Hub sync sees.</StateMessage>;
  const steps = mockStatusTimeline(casino);
  return (
    <Card title="Status changes" aside={<MadeUp what="Status changes" />} notYet="history">
      <ol className="space-y-4">
        {steps.map((s, i) => (
          <li key={s.label} className="flex gap-3">
            <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${i === steps.length - 1 ? 'bg-brand' : 'bg-fg-faint'}`} aria-hidden />
            <div>
              <p className="text-sm text-fg-strong">
                {s.label}
                <span className="ml-2 text-xs text-fg-faint">{formatDate(s.at)}</span>
              </p>
              <p className="text-sm text-fg-muted">{s.detail}</p>
            </div>
          </li>
        ))}
      </ol>
      <p className="mt-4 text-xs text-fg-faint">Prisma updates a casino from the platform database only; nobody edits its status by hand (CAS-2, CAS-4).</p>
    </Card>
  );
};

/**
 * One casino (CAS-1…4, SEG-1…3, OUT-1…6). The header and the 30-day totals come
 * from GET /casinos/{id}, the segments from GET /casinos/{id}/segments. The messages and
 * the timeline are made up and marked Demo until those parts are built, and only locally
 * (showDemo); elsewhere they say "No data yet".
 */
const CasinoPage = () => {
  const { id = '', tab } = useParams();
  const { data: casino, error } = useApi<CasinoDetail>(`/casinos/${encodeURIComponent(id)}`);
  const base = `/casinos/${encodeURIComponent(id)}`;
  const back = { to: '/casinos', label: 'Casinos' };

  if (tab && !TABS.includes(tab as Tab)) return <Navigate to={base} replace />;
  const current = (tab as Tab | undefined) ?? 'overview';

  if (error && error !== 'Not found.') {
    return (
      <div>
        <PageHeader title="Casino" back={back} />
        <StateMessage title="The casino could not be loaded" tone="error">
          {error}
        </StateMessage>
      </div>
    );
  }
  if (!casino && !error) {
    return (
      <div>
        <PageHeader title="Loading…" back={back} />
        <div className="h-40 animate-pulse rounded-card bg-surface shadow-e1" />
      </div>
    );
  }
  if (!casino) {
    return (
      <div>
        <PageHeader title="Casino not found" back={back} />
        <StateMessage title="No casino with this id">
          It may have been removed from Owner Hub.{' '}
          <Link to="/casinos" className={btn.link}>
            Back to Casinos
          </Link>
        </StateMessage>
      </div>
    );
  }

  const facts = [casino.domain, casino.country && countryName(casino.country), casinoStatusLabel(casino.status)].filter(Boolean).join(' · ');
  return (
    <div>
      <PageHeader
        back={back}
        title={casino.brand_name || 'Unnamed casino'}
        description={
          <>
            {facts}
            {casino.owner_email && (
              <>
                {' · owner '}
                <Link to={leadSearchLink(casino.owner_email)} className="text-brand [overflow-wrap:anywhere] hover:underline">
                  {casino.owner_email}
                </Link>
              </>
            )}
          </>
        }
        actions={<ActivityBadge activity={casino.activity} />}
      >
        <SubNav
          label="Casino"
          items={[
            { to: base, label: 'Overview', end: true },
            { to: `${base}/segments`, label: 'Segments' },
            { to: `${base}/messages`, label: 'Messages' },
            { to: `${base}/timeline`, label: 'Timeline' },
          ]}
        />
      </PageHeader>
      {current === 'overview' && <Overview casino={casino} mock={casino.metrics_source === 'mock'} />}
      {current === 'segments' && <Segments casino={casino} />}
      {current === 'messages' && <Messages casino={casino} />}
      {current === 'timeline' && <Timeline casino={casino} />}
    </div>
  );
};

export default CasinoPage;
